import {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import dayjs, {Dayjs} from 'dayjs';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useRqGetRecordingTaskMessages} from '../../../../react_query/messages/task/ListRecordingMessages';
import DialogNotes from '../../../../components/dialogs/DialogNotes';
import {apiPost} from '../../../../axiosConfig';
import DialogApiRequestStatus from '../../../../components/dialogs/DialogApiRequestStatus';
import MusicPreviewBtn from '../../../../components/music/MusicPreviewBtn';
import {IMediaTrack} from '../../../../dto/MediaTrack.dto';
import ExistingCampaignChooserBtn from '../../../sponsor/ExistingCampaignChooserBtn';
import {SponsorRequestDetailsCard} from '../../../sponsor/SponsorshipRequest';
import {
  IMultipleFileUploadProp,
  MultipleFileUploadCmpt,
} from '../../../sponsor/MultipleFileUploadCmpt';
import {ConditionalRender, UserCanAny} from '../../../../components/UserCan';
import UploadButton from '../../../../components/styles/UploadButton';
import DownloadButton from '../../../../components/Button/DownloadButton';
import {broadCastCloseTab} from '../../../../react_query/tasks/ListTasks';
import {useTaskContext} from '../../../task/TaskContext';
import {SponsorMessageDetailsCmpt} from '../../../admin/CMS_OPS/VoiceArtist/onboard/Recording';
import {
  duplicateFileNameExist,
  multipleFileUploadValidation,
  nameIncrementing,
} from '../../../sponsor/SponsorRequest';
import {useRqSponsorRequestDetails} from '../../../../react_query/sponsor_commercial/SponsorRequestDetails';
import {sponsorCommercialAppendTrackInfo} from './sponsorCommercialAppendTrackInfo';

const AudioApproval = () => {
  const task = useParams();
  const taskId = Number(task.task_id);
  const {data} = useRqGetRecordingTaskMessages(taskId);
  const [open, setOpen] = useState(false);
  const [message, SetMessage] = useState('');
  const [type, setType] = useState('');
  const [apiState, setApiState] = useState({status: '', error: null});
  const {companyId} = useTaskContext();
  const [selectedTrack, setSelectedTrack] = useState<
    (IMediaTrack & {created_at?: string}) | null
  >(null);
  const [file, setFile] = useState<File | null>();
  const [multipleFileUpload, setMultipleFileUpload] = useState<
    IMultipleFileUploadProp[]
  >([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [stepperLoadedOn, setStepperLoadedOn] = useState<Dayjs | null>(null);
  useEffect(() => {
    setStepperLoadedOn(dayjs());
  }, []);

  const handleSubmit = () => {
    handleApproveStatus(type);
  };
  const handleApproveStatus = (type: string) => {
    if (
      type === 'reassign' &&
      data?.requestable.message?.is_manual_upload === 1 &&
      data?.task_slug !== 'sponsor-file-review'
    ) {
      type = 'file-reassign';
    } else if (
      type === 'reassign' &&
      data?.task_slug === 'sponsor-file-review'
    ) {
      type = 'partner-reassign';
    }

    setType(type);
    const fd = new FormData();
    fd.append('type', type);
    fd.append('message', message);
    sponsorCommercialAppendTrackInfo(fd, selectedTrack, stepperLoadedOn);
    if (file) {
      fd.append('file', file);
    }

    if (
      ['verify-sponsor-provided-file', 'verify-partner-provided-file'].includes(
        String(data?.task_slug),
      ) &&
      multipleFileUpload.length > 0
    ) {
      multipleFileUpload.forEach((val, key) => {
        fd.append(`multiple_files[${key}][file_name]`, val.fileName);
        fd.append(`multiple_files[${key}][file]`, val.file as Blob);
      });
    }

    apiPost(`/v1/task/${taskId}/audio/verification`, fd)
      .then(() => {
        setApiState({
          status: 'success',
          error: null,
        });
        broadCastCloseTab();
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  };

  const handleChange = useCallback(
    (
      value: string | File | undefined,
      field: 'request_format' | 'files' | 'message',
    ) => {
      if (field === 'files' && typeof value === 'object') {
        setFile(value);
        setSelectedTrack(null);
      }
    },
    [setFile],
  );

  const handleMessage = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    SetMessage(event.target.value);
  }, []);
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null});
  }, []);
  const handleClose = () => {
    setOpen(false);
    SetMessage('');
  };
  const onClose = useCallback(() => {
    setApiState({status: 'pending', error: null});
    broadCastCloseTab();
  }, []);

  const handleDeleteOfExistSong = useCallback(() => {
    setSelectedOption(null);
    setSelectedTrack(null);
  }, []);

  useEffect(() => {
    if (selectedTrack) {
      setSelectedOption(null);
    }
  }, [selectedTrack]);

  const {data: sponsorInfo} = useRqSponsorRequestDetails(
    data?.requestable_id ?? 0,
    data?.requestable_type === 'sponsor-commercial' &&
      Boolean(data?.requestable_id),
  );

  useEffect(() => {
    if (selectedOption && setMultipleFileUpload) {
      setMultipleFileUpload(
        nameIncrementing(
          selectedOption,
          sponsorInfo?.sponsor.sponsor_name || '',
        ),
      );
    } else {
      setMultipleFileUpload([]);
    }
  }, [
    selectedOption,
    setMultipleFileUpload,
    sponsorInfo?.sponsor.sponsor_name,
  ]);

  const isDisabled = useCallback(() => {
    return (
      multipleFileUploadValidation(multipleFileUpload) &&
      !duplicateFileNameExist(multipleFileUpload)
    );
  }, [multipleFileUpload]);

  return (
    <Box className='p-3 m-2 blue-variant17'>
      <Box className='ml-2'>
        {data?.requestable_id &&
        data.requestable_type === 'sponsor-commercial' ? (
          <UserCanAny scope='is-admin,sponsor:view,partner'>
            <SponsorRequestDetailsCard sponsorRequestId={data.requestable_id} />
          </UserCanAny>
        ) : null}
        <Stack direction={'row'} className='mb-2'>
          <Typography className='w-248 text-bold'>Last Updated</Typography>
          <Typography>
            :{' '}
            {dayjs(data?.requestable.created_at).format(
              'MM/DD/YYYY hh:mm:ss A',
            )}
          </Typography>
        </Stack>
        <Stack direction={'row'} className='mb-2'>
          <Typography className='w-248 text-bold'>Submitted At</Typography>
          <Typography>
            :{' '}
            {dayjs(data?.requestable.updated_at).format(
              'MM/DD/YYYY hh:mm:ss A',
            )}
          </Typography>
        </Stack>
        {data?.task_slug === 'production-file-review' &&
          data?.requestable.ai_voice &&
          data?.requestable.ai_voice_file && (
            <>
              <Stack direction={'row'} className='mb-2'>
                <Typography className='w-248 text-bold'>Ai Voice</Typography>{' '}
                <Typography>: {data?.requestable.ai_voice}</Typography>
              </Stack>
              <Stack direction={'row'} className='my-4 mt-3' gap={1}>
                <Typography className='w-248 text-bold'>
                  CopyWriter attached Version
                </Typography>
                {data?.requestable.ai_voice_file && (
                  <>
                    <MusicPreviewBtn
                      id={'ai-generated-voice'}
                      url={data?.requestable.ai_voice_file?.file_url ?? ''}
                      track_name={
                        data?.requestable.ai_voice_file?.file_name ?? ''
                      }
                      track_artist={data?.requestable.ai_voice}
                    />
                    <DownloadButton
                      filename={`${data?.requestable.ai_voice}_${data?.requestable.ai_voice_file?.file_name}_${data?.requestable_id}.mp3`}
                      url={data?.requestable.ai_voice_file?.file_url ?? ''}
                    />
                  </>
                )}
              </Stack>
            </>
          )}
      </Box>

      {data?.task_comments &&
        data?.task_comments.length > 0 &&
        data?.task_comments.map(value => (
          <ListItem key={value.id}>
            <Typography
              variant='h6'
              aria-level={2}
              component='div'
              style={{width: '20%'}}>
              Task Description
            </Typography>
            <Typography variant='subtitle1' component='div'>
              {value.comments}
            </Typography>
          </ListItem>
        ))}
      {data?.requestable &&
      data?.requestable.messages &&
      data?.requestable?.messages.length > 1 ? (
        data?.requestable.messages.map(val => (
          <ConditionalRender condition={Boolean(val.initial_url)}>
            <Stack key={val.id} direction='row' spacing={4} mb={2}>
              <Typography
                aria-label='audio_file_name'
                variant='h5'
                aria-level={2}
                sx={{p: 1}}>
                {val.prepopulated_file_name}
              </Typography>
              {val.initial_url ? (
                <MusicPreviewBtn
                  id={val.id}
                  url={val.initial_url}
                  track_name={val?.media_track?.track_name}
                  track_artist={val?.media_track?.track_artist}
                />
              ) : null}
              <IconButton
                component='a'
                href={val.initial_url}
                target='_blank'
                download={val.prepopulated_file_name + '.mp3'}
                rel='noreferrer'
                aria-label='Download Audio File'>
                <DownloadIcon />
              </IconButton>
            </Stack>
          </ConditionalRender>
        ))
      ) : (
        <ConditionalRender
          condition={Boolean(data?.requestable.message?.initial_url)}>
          <Stack direction='row' spacing={4} mr={2} mt={2}>
            <Typography
              aria-label='audio_file_name'
              variant='h5'
              aria-level={2}
              sx={{p: 1}}>
              {data?.requestable?.message?.prepopulated_file_name}
            </Typography>
            {data?.requestable.message?.initial_url ? (
              <MusicPreviewBtn
                id={data.id}
                url={data?.requestable.message?.initial_url}
                track_name={
                  data?.requestable.message?.media_track?.track_name ||
                  data?.requestable?.message?.prepopulated_file_name
                }
                track_artist={
                  data?.requestable.message?.media_track?.track_artist
                }
              />
            ) : null}
            <IconButton
              component='a'
              href={data?.requestable?.message?.initial_url}
              target='_blank'
              download={
                data?.requestable?.message?.prepopulated_file_name + '.mp3'
              }
              rel='noreferrer'
              aria-label='Download Audio File'>
              <DownloadIcon />
            </IconButton>
          </Stack>
        </ConditionalRender>
      )}
      {data?.requestable.message ? (
        <SponsorMessageDetailsCmpt message={data?.requestable.message} />
      ) : null}
      {[
        'verify-sponsor-provided-file',
        'verify-partner-provided-file',
      ].includes(String(data?.task_slug)) ? (
        <MultipleFileUploadCmpt
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          multipleFileUpload={multipleFileUpload}
          setMultipleFileUpload={setMultipleFileUpload}
          isDisabled={Boolean(selectedTrack?.id)}
        />
      ) : null}
      {[
        'verify-sponsor-provided-file',
        'verify-partner-provided-file',
        'production-file-review',
      ].includes(String(data?.task_slug)) ? (
        <Stack direction='row' spacing={1} className='ml-1'>
          {![
            'verify-sponsor-provided-file',
            'verify-partner-provided-file',
          ].includes(String(data?.task_slug)) ? (
            <UploadButton
              label='Upload Unprocessed File'
              handleChange={handleChange}
            />
          ) : null}
          <ExistingCampaignChooserBtn
            disabled={Number(selectedOption) > 0}
            tracks={[]}
            onChange={(tracks: IMediaTrack[]) => {
              if (tracks.length > 0) {
                setSelectedTrack(tracks[0] as IMediaTrack);
                setFile(null);
              }
            }}
            label='Choose Sponsor Commercial'
            fileUploadLabel='Attach Processed File'
            type='sponsor-commercial'
            max={1}
            companyId={companyId}
          />
        </Stack>
      ) : null}
      {selectedTrack ? (
        <Stack
          className='p-2 radius-5 mt-2 flex-row items-center justify-between max-w-550 w-full mb-2 ml-2'
          sx={{border: '1px solid var(--gray-opacity-variant2)'}}>
          <Typography>{selectedTrack?.track_name}</Typography>
          <MusicPreviewBtn
            id={selectedTrack.id}
            url={selectedTrack.url}
            key={selectedTrack.url}
            track_name={selectedTrack?.track_name}
            track_artist={selectedTrack?.track_artist}
          />
          <IconButton onClick={handleDeleteOfExistSong}>
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      ) : null}

      {file ? <FilePreview file={file} setFile={setFile} /> : null}

      <Stack direction='row' spacing={2} justifyContent={'flex-end'}>
        <Button
          variant='outlined'
          disabled={
            String(data?.task_slug) === 'verify-partner-provided-file' &&
            multipleFileUpload.length > 0
              ? !isDisabled()
              : false
          }
          onClick={() => {
            handleApproveStatus(
              data?.task_slug === 'sponsor-file-review'
                ? 'partner-approval'
                : data?.requestable.message?.is_manual_upload === 1
                ? 'file-approval'
                : 'approved',
            );
          }}>
          Approve
        </Button>
        {data?.task_slug &&
        ![
          'verify-sponsor-provided-file',
          'verify-partner-provided-file',
        ].includes(data?.task_slug) ? (
          <Button variant='outlined' onClick={() => setOpen(true)}>
            {data?.requestable.message?.is_manual_upload === 0
              ? 'Re-assign'
              : 'Assign for Reupload'}
          </Button>
        ) : null}
      </Stack>

      <DialogNotes
        open={open}
        note={message}
        handleChange={handleMessage}
        handleSubmit={handleApproveStatus}
        handleClose={handleClose}
      />
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSubmit}
          onEdit={handleEdit}
          onClose={onClose}
        />
      )}
    </Box>
  );
};

export default AudioApproval;

export const FilePreview = ({
  file,
  setFile,
}: {
  file: File;
  setFile: (val: File | null) => void;
}) => {
  const handleDelete = () => {
    setFile(null);
  };

  const url = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [url]);

  return (
    <List dense={false} sx={{mt: 1}}>
      <ListItem
        sx={{background: '#0d1525'}}
        className='cursor-pointer'
        secondaryAction={
          <IconButton onClick={handleDelete}>
            <DeleteOutlineIcon />
          </IconButton>
        }>
        <ListItemIcon>
          <MusicPreviewBtn
            id={url}
            url={url}
            track_name={file.name}
            track_artist={file.name}
          />
        </ListItemIcon>
        <ListItemText primary={file.name} />
      </ListItem>
    </List>
  );
};
